/**
 * Servicio de API para red vial base (vías de referencia IGN)
 *
 * @module core/spatial/services
 * @version 1.0
 * @created 2026-03-01
 */

import axios from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

// Log BASE_URL once on module initialization for diagnostics
console.log('[base-network-api] BASE_URL:', BASE_URL);

/**
 * Obtiene el token JWT del localStorage
 */
function getAuthToken(): string | null {
  try {
    const authData = localStorage.getItem('auth-storage');
    if (!authData) return null;
    const parsed = JSON.parse(authData);
    return parsed.state?.token || null;
  } catch {
    return null;
  }
}

/**
 * Categoría de vía según IGN Argentina
 */
export type RoadCategory =
  | 'autopista_010'
  | 'autopista_050'
  | 'nacional'
  | 'provincial'
  | 'terciaria';

/**
 * Interfaz de vía base
 */
export interface BaseRoadNetwork {
  id: string;
  country: string;
  category: RoadCategory;
  geometry: {
    type: 'MultiLineString';
    coordinates: number[][][];
  };
  properties: Record<string, any> | null;
  source: string;
  sourceVersion: string | null;
  createdAt: string;
}

/**
 * GeoJSON FeatureCollection de vías base
 */
export interface BaseNetworkGeoJSON {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    id: string;
    geometry: {
      type: 'MultiLineString';
      coordinates: number[][][];
    };
    properties: {
      category: RoadCategory;
      country: string;
      source: string;
      sourceVersion: string | null;
      [key: string]: any;
    };
  }>;
}

/**
 * Estadísticas de vías base
 */
export interface BaseNetworkStats {
  total: number;
  byCategory: Record<RoadCategory, number>;
}

/**
 * Servicio de API para vías base de referencia
 *
 * @remarks
 * Cliente HTTP para endpoints de red vial base (IGN, OSM, municipal).
 * Estas vías son de solo lectura y sirven como capa de referencia.
 */
export const baseNetworkApi = {
  /**
   * Importa vías base del IGN Argentina para un proyecto
   *
   * @param projectId - ID del proyecto
   * @returns Resultado de la importación
   *
   * @remarks
   * Endpoint temporal para proyectos existentes.
   * Los nuevos proyectos importan automáticamente.
   *
   * @example
   * const result = await baseNetworkApi.importArgentina('abc-123');
   * console.log(`Importadas ${result.imported} vías`);
   */
  async importArgentina(projectId: string): Promise<{
    success: boolean;
    message: string;
    imported: number;
    byCategory: Record<RoadCategory, number>;
  }> {
    const url = `${BASE_URL}/spatial/base-network-import/${projectId}/argentina`;
    const token = getAuthToken();
    const response = await axios.post(url, {}, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },
  /**
   * Obtiene todas las vías base de un proyecto
   *
   * @param projectId - ID del proyecto
   * @param category - Filtrar por categoría (opcional)
   * @returns Array de vías base
   *
   * @example
   * const roads = await baseNetworkApi.getByProject('abc-123');
   * const nacionales = await baseNetworkApi.getByProject('abc-123', 'nacional');
   */
  async getByProject(
    projectId: string,
    category?: RoadCategory,
  ): Promise<BaseRoadNetwork[]> {
    const url = `${BASE_URL}/spatial/base-network/${projectId}`;
    const params = category ? { category } : {};
    const token = getAuthToken();

    const response = await axios.get<BaseRoadNetwork[]>(url, {
      params,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },

  /**
   * Obtiene las vías base de un proyecto en formato GeoJSON
   *
   * @param projectId - ID del proyecto
   * @param category - Filtrar por categoría (opcional)
   * @returns FeatureCollection GeoJSON
   *
   * @remarks
   * Formato optimizado para visualización en MapLibre
   *
   * @example
   * const geojson = await baseNetworkApi.getGeoJSON('abc-123');
   * map.getSource('base-roads').setData(geojson);
   */
  async getGeoJSON(
    projectId: string,
    category?: RoadCategory,
  ): Promise<BaseNetworkGeoJSON> {
    const url = `${BASE_URL}/spatial/base-network/${projectId}/geojson`;
    const params = category ? { category } : {};
    const token = getAuthToken();

    const response = await axios.get<BaseNetworkGeoJSON>(url, {
      params,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      timeout: 30000, // 30s — GeoJSON puede ser grande
    });
    return response.data;
  },

  /**
   * Obtiene estadísticas de vías base de un proyecto
   *
   * @param projectId - ID del proyecto
   * @returns Estadísticas por categoría
   *
   * @example
   * const stats = await baseNetworkApi.getStats('abc-123');
   * console.log(`Total: ${stats.total}, Nacionales: ${stats.byCategory.nacional}`);
   */
  async getStats(projectId: string): Promise<BaseNetworkStats> {
    const url = `${BASE_URL}/spatial/base-network/${projectId}/stats`;
    const token = getAuthToken();
    const response = await axios.get<BaseNetworkStats>(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },
};
